import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { CONTACT } from "../content";
import { EASE } from "./ui-bits";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/services", label: "Services" },
    { to: "/compliance-guide", label: "Compliance Guide" },
    { to: "/about", label: "About" },
];

export function Nav() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <>
            <header
                data-testid="site-nav"
                className="sticky top-0 z-50 border-b border-inkline bg-bone/80 backdrop-blur-xl"
            >
                <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-10">
                    <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center bg-bluebrand font-display text-sm font-bold text-white transition-colors duration-300 group-hover:bg-ink">
                            LA
                        </span>
                        <span className="leading-none">
                            <span className="block font-display text-sm font-bold tracking-tight">LOS ANGELES</span>
                            <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-smoke">
                                ADA Signage Co.
                            </span>
                        </span>
                    </Link>

                    <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `relative text-sm font-semibold transition-colors duration-300 hover:text-bluebrand ${
                                        isActive ? "text-bluebrand" : "text-ink"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <a
                            href={`tel:${CONTACT.phone.replace(/[^0-9]/g, "")}`}
                            data-testid="nav-phone"
                            className="hidden text-sm font-semibold text-smoke transition-colors duration-300 hover:text-bluebrand md:block"
                        >
                            {CONTACT.phone}
                        </a>
                        <Link
                            to="/get-started"
                            data-testid="nav-cta"
                            className="hidden items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-bone transition-[background-color,transform] duration-300 hover:bg-bluebrand active:scale-[0.97] sm:inline-flex"
                        >
                            Get Started
                            <ArrowUpRight className="h-3.5 w-3.5" />
                        </Link>
                        <button
                            data-testid="nav-menu-toggle"
                            onClick={() => setOpen(!open)}
                            className="flex h-10 w-10 items-center justify-center border border-inkline lg:hidden"
                            aria-label="Toggle menu"
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="fixed inset-x-0 top-[73px] z-40 border-b border-inkline bg-bone/95 backdrop-blur-xl lg:hidden"
                    >
                        <nav className="flex flex-col px-6 py-6">
                            {[...LINKS, { to: "/get-started", label: "Get Started" }].map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.4, ease: EASE }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="flex items-center justify-between border-b border-inkline py-4 font-display text-2xl font-bold tracking-tight"
                                    >
                                        {l.label}
                                        <ArrowUpRight className="h-5 w-5 text-bluebrand" />
                                    </NavLink>
                                </motion.div>
                            ))}
                            <p className="pt-5 text-sm font-semibold text-smoke">{CONTACT.phone} — {CONTACT.area}</p>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
